import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { getGuideBySlug, getRelatedGuides } from '../data/govtGuidesData';
import ContentRenderer from './ContentRenderer';
import Navbar from './Navbar';
import Footer from './Footer';

const GovtGuidePost = () => {
  const { slug } = useParams();
  const guide = getGuideBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (guide) {
      document.title = `${guide.title} | Govt Guides`;
    }
  }, [slug, guide]);

  if (!guide) {
    return <Navigate to="/govt-guides" replace />;
  }

  const Icon = Icons[guide.icon] || Icons.FileText;
  const related = getRelatedGuides(slug);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="govt-guide-article">
        <Link to="/govt-guides" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition mb-8" data-testid="govt-back-link">
          <Icons.ArrowLeft size={16} /> Back to all guides
        </Link>

        <div className="flex items-start gap-4 mb-6">
          <div className="shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center text-orange-700">
            <Icon size={26} />
          </div>
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-medium mb-3">
              <Icons.Tag size={12} /> {guide.category}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-gray-900" data-testid="govt-guide-title">
              {guide.title}
            </h1>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-500 pb-6 border-b border-gray-200">
          <span className="flex items-center gap-1.5"><Icons.Clock size={14} /> {guide.readTime}</span>
          <span className="flex items-center gap-1.5"><Icons.Calendar size={14} /> Updated {guide.updated}</span>
          <span className="text-gray-300">·</span>
          <span>By the MedCertGen editorial team</span>
        </div>

        <p className="mt-8 text-lg text-gray-700 leading-relaxed font-medium">{guide.summary}</p>

        <div className="mt-6">
          <ContentRenderer content={guide.content} />
        </div>

        <div className="mt-12 p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100">
          <h3 className="text-xl font-semibold text-gray-900">Looking for related government documents?</h3>
          <p className="mt-2 text-sm text-gray-600">Browse our complete library of Indian government guides — schemes, certificates, leave rules and welfare programmes.</p>
          <Link to="/govt-guides" data-testid="govt-cta-browse-all" className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition">
            Browse all guides <Icons.ChevronRight size={16} />
          </Link>
        </div>
      </article>

      {related.length > 0 && (
        <section className="border-t border-gray-200 bg-gray-50 py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Related guides</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="related-guides">
              {related.map((r) => {
                const Icn = Icons[r.icon] || Icons.FileText;
                return (
                  <Link key={r.slug} to={`/govt-guides/${r.slug}`} data-testid={`related-govt-${r.slug}`} className="block p-5 rounded-xl bg-white border border-gray-200 hover:border-orange-400 hover:shadow transition">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center text-orange-700">
                        <Icn size={16} />
                      </div>
                      <div className="text-xs text-orange-700 font-medium">{r.category}</div>
                    </div>
                    <h3 className="font-semibold text-gray-900 text-base leading-snug">{r.title}</h3>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default GovtGuidePost;
